import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { Card, CardContent, Box, Stack, Button, } from '@mui/material';
import { useTokens } from '../../tokens/TokensProvider';
import { useConversationStore } from '../../ConversationStoreProvider';
import { replaceStylesheetGlobals } from '../../portal/theme/portalTheme';
import { mapProductCardConfigToFormatting } from './productCardConfig';
import { nearestLocationDistanceMiles } from './haversine';
import useProductCardResponsive from '../hooks/useProductCardResponsive';
import { useIsMobileViewContext } from '../IsMobileViewContext';
import getProductData from './getProductData';
import CardImage from './CardImage';
import ProductTitle from './ProductTitle';
import ProductPrice from './ProductPrice';
import DescriptionRenderer from './DescriptionRenderer';
import VariantSelector from './VariantSelector';
import QuantityField from './QuantityField';
import AddToCartButton from './AddToCartButton';
import CheckoutFlow from './CheckoutFlow';
import { buildVariantImageDictionary as sharedBuildVariantImageDictionary, getSelectedVariant, getVariantOptionAtPosition, shouldAutoSelectVariant, syncVariantStates, getVariantImageUrl, getBestMatchingVariant, getButtonState, } from './variantValidationUtils';
import { createEmbedMessageHandler } from './embedMessageHandler';
const normalizeVariantId = (variantId) => (variantId == null ? '' : String(variantId).trim());
const recordsEqual = (a, b) => {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    return aKeys.length === bKeys.length
        && aKeys.every((key) => a[key] === b[key]);
};
// Helper function to resolve configuration with priority: props > aiSearchConfig > defaults
// When isDualModeEnabled is false, ignores aiSearchConfigValue to maintain retrocompatibility
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function resolveConfig(propValue, aiSearchConfigValue, defaultValue, isDualModeEnabled = false) {
    // When dual mode is OFF, ignore aiSearchConfig to maintain retrocompatibility
    if (!isDualModeEnabled) {
        return propValue ?? defaultValue;
    }
    return propValue ?? aiSearchConfigValue ?? defaultValue;
}
// eslint-disable-next-line no-empty-pattern, arrow-body-style
const Ratings = ({}) => {
    // if (!showRatings || !enhancedData.rating) return null;
    // return (
    //   <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
    //     <Rating
    //       value={enhancedData.rating}
    //       readOnly
    //       sx={{ color: '#FFD700', mr: 1 }}
    //     />
    //     <Typography
    //       variant="body2"
    //       sx={{ color: '#1976d2', cursor: 'pointer' }}
    //     >
    //       {enhancedData.rating.toFixed(1)}
    //       {' '}
    //       (
    //       {enhancedData.reviewCount || 0}
    //       {' '}
    //       Reviews)
    //     </Typography>
    //   </Box>
    // );
    // Ratings temporarily disabled until we have a proper integration
    return null;
};
/** Build card state from a Shopify-shaped productPayload (cents) so first paint matches full UI when data is preloaded. */
function hydrateFromProductPayload(jsonData, fallbacks) {
    if (!jsonData || typeof jsonData !== 'object')
        return null;
    let apiImage = jsonData?.image;
    if (jsonData?.featured_image) {
        apiImage = jsonData.featured_image.startsWith('//')
            ? `https:${jsonData.featured_image}`
            : jsonData.featured_image;
    }
    else if (jsonData?.images?.[0]) {
        const img0 = jsonData.images[0];
        apiImage = typeof img0 === 'string'
            ? (img0.startsWith('//') ? `https:${img0}` : img0)
            : (img0?.url || img0);
    }
    const priceCents = (jsonData.price_min ?? jsonData.price);
    const fromPayload = priceCents != null && priceCents > 0 ? priceCents / 100 : null;
    const currentPrice = fromPayload ?? (fallbacks.price != null && fallbacks.price > 0 ? fallbacks.price : null);
    const enhancedData = {
        ...(jsonData || {}),
        image: apiImage || fallbacks.image,
        rating: undefined,
        reviewCount: undefined,
        originalPrice: jsonData?.compare_at_price ? jsonData.compare_at_price / 100 : null,
        currentPrice,
        description: jsonData?.description || fallbacks.description || '',
        title: jsonData?.title || fallbacks.title,
    };
    let normalized = [];
    let groups = [];
    if (jsonData.variants && jsonData.options) {
        normalized = jsonData.variants.map((variant) => ({
            id: variant.id.toString(),
            sku: variant.sku || '',
            price: variant.price / 100,
            displayTag: variant.name ?? variant.title,
            available: variant.available,
            options: variant.options,
            option1: variant.option1,
            option2: variant.option2,
            option3: variant.option3,
            featured_image: variant.featured_image,
            featured_media: variant.featured_media,
        }));
        groups = jsonData.options.map((option, optionIndex) => ({
            name: option.name,
            type: option.name.toLowerCase(),
            options: option.values.map((value, index) => {
                const isAvailable = jsonData.variants.some((variant) => {
                    const atPos = getVariantOptionAtPosition(variant, optionIndex);
                    return atPos != null
                        && String(atPos).trim() === String(value).trim()
                        && variant.available !== false;
                });
                return {
                    id: `${option.name}-${index}`,
                    value,
                    available: isAvailable,
                };
            }),
            required: true,
        }));
    }
    return { enhancedData, normalizedVariants: normalized, dynamicVariantGroups: groups };
}
const EnhancedProductCard = ({ title, image, url, customStyle, productFormatting, aiSearchConfig, price, description, onProductClick, product, forceVertical, initialVariantId, }) => {
    const store = useConversationStore();
    const tokens = useTokens();
    // Get locale from customerContext, fallback to 'en'
    const locale = useMemo(() => store?.customerContext?.currentPage?.locale
        || store?.customerContext?.locale
        || 'en', [store?.customerContext]);
    // Get currency from customerContext, fallback to 'USD'
    const currency = useMemo(() => store?.customerContext?.currentPage?.currency
        || store?.customerContext?.currency
        || 'USD', [store?.customerContext]);
    const layoutMode = store?.widgetProps?.layoutMode || 'ai';
    // Detect dual mode feature flag only when layoutMode is dual
    const isDualModeEnabled = Boolean(store?.clientAIConfig?.clientAIUseDualSearch)
        && layoutMode === 'dual';
    const normalizedInitialVariantId = useMemo(() => normalizeVariantId(initialVariantId), [initialVariantId]);
    const [selectedVariantId, setSelectedVariantId] = useState(normalizedInitialVariantId);
    const [selectedVariantOptions, setSelectedVariantOptions] = useState({});
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const preloadedPayload = product?.productPayload && typeof product.productPayload === 'object'
        ? product.productPayload
        : null;
    const initialHydration = preloadedPayload
        ? hydrateFromProductPayload(preloadedPayload, {
            title,
            image,
            description,
            price,
        })
        : null;
    const [isLoadingProduct, setIsLoadingProduct] = useState(() => !initialHydration);
    const [quantity, setQuantity] = useState(1);
    // Dynamic variant data
    const [dynamicVariantGroups, setDynamicVariantGroups] = useState(() => initialHydration?.dynamicVariantGroups ?? []);
    const [productData, setProductData] = useState(() => initialHydration?.enhancedData ?? null);
    const [normalizedVariants, setNormalizedVariants] = useState(() => initialHydration?.normalizedVariants ?? []);
    const [isAddedToCart, setIsAddedToCart] = useState(false);
    const checkoutFlowRef = useRef(null);
    // Image tracking for variant swatching
    const [currentImageUrl, setCurrentImageUrl] = useState('');
    const [hoverImageUrl, setHoverImageUrl] = useState('');
    const [mainImageUrl, setMainImageUrl] = useState('');
    const findBestImageForSelection = useCallback((selectedOptions, variants, productDataParam) => {
        if (!productDataParam || variants.length === 0) {
            return null;
        }
        // Use shared helper for intelligent matching (exact or partial)
        const matchResult = getBestMatchingVariant(selectedOptions, variants, productDataParam);
        if (matchResult) {
            const imageUrl = getVariantImageUrl(matchResult.variant, productDataParam, image);
            if (imageUrl) {
                const imageDictionary = sharedBuildVariantImageDictionary(productDataParam);
                const entry = imageDictionary[matchResult.variant.id];
                return {
                    imageUrl,
                    score: entry?.specificity || (matchResult.matchType === 'exact' ? 10000 : 5000),
                    matchType: matchResult.matchType,
                    matchedOptions: Object.values(selectedOptions),
                };
            }
        }
        // Final fallback using existing logic
        const fallbackImage = productDataParam.image || productDataParam.images?.[0]?.url || image;
        if (fallbackImage) {
            return {
                imageUrl: fallbackImage,
                score: 100,
                matchType: 'fallback',
                matchedOptions: [],
            };
        }
        return null;
    }, [image]);
    // Update image when variant selection changes (permanent selection)
    useEffect(() => {
        if (Object.keys(selectedVariantOptions).length > 0) {
            const bestImage = findBestImageForSelection(selectedVariantOptions, normalizedVariants, productData);
            if (bestImage && bestImage.imageUrl !== currentImageUrl) {
                setCurrentImageUrl(bestImage.imageUrl);
            }
        }
    }, [
        selectedVariantOptions,
        findBestImageForSelection,
        normalizedVariants,
        productData,
        currentImageUrl,
    ]);
    // Handle hover preview for variant options
    const handleOptionHover = useCallback((payload) => {
        const bestImage = findBestImageForSelection(payload.previewSelection, normalizedVariants, productData);
        if (bestImage && bestImage.imageUrl !== currentImageUrl) {
            setHoverImageUrl(bestImage.imageUrl);
        }
    }, [findBestImageForSelection, normalizedVariants, productData, currentImageUrl]);
    // Handle when hover ends
    const handleOptionLeave = useCallback(() => {
        setHoverImageUrl('');
    }, []);
    const handleVariantImageChange = useCallback((imageUrl, context) => {
        if (context.action === 'hover') {
            // For hover, use the existing hover state
            setHoverImageUrl(imageUrl || '');
        }
        else if (context.action === 'leave') {
            // Clear hover
            setHoverImageUrl('');
        }
        else if (context.action === 'select') {
            // For selection, update the main current image
            if (imageUrl) {
                setCurrentImageUrl(imageUrl);
            }
        }
        else if (context.action === 'initial') {
            // For initial load, set the main image
            if (imageUrl) {
                setMainImageUrl(imageUrl);
            }
        }
        // Always provide fallback logic
        if (!imageUrl && (context.action === 'select' || context.action === 'initial')) {
            let fallback = image;
            if (productData?.featured_image) {
                fallback = productData.featured_image.startsWith('//')
                    ? `https:${productData.featured_image}`
                    : productData.featured_image;
            }
            if (context.action === 'initial') {
                setMainImageUrl(fallback);
            }
            else {
                setCurrentImageUrl(fallback);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainImageUrl, productData, image]);
    // Responsive logic
    const { finalOrientation: responsiveFinalOrientation, forceVertical: responsiveForceVertical, shouldApplyResponsiveStyles, isTinyScreen, cardWidth, } = useProductCardResponsive({ clientAIConfig: store.clientAIConfig });
    const isMobileView = useIsMobileViewContext();
    // Unique messageKey for this ProductCard instance - STABLE
    // const messageKey = useMemo(() => {
    //   const handle = url.split('/').pop();
    //   return `product-${handle}`;
    // }, [url]);
    // Unique instance ID for this specific ProductCard instance - STABLE
    // const instanceId = useRef(`${messageKey}-${Math.random().toString(36).substr(2, 9)}`).current;
    // Listen for variant details response
    useEffect(() => {
        const handleVariantDetails = (event) => {
            if (event.data.type === 'variantDetailsResponse' && event.data.productId === url) {
                const { variantGroups: responseVariantGroups } = event.data;
                setDynamicVariantGroups(responseVariantGroups || []);
            }
        };
        window.addEventListener('message', handleVariantDetails);
        return () => window.removeEventListener('message', handleVariantDetails);
    }, [url]);
    const updateVariantData = async (externalProductData) => {
        if (externalProductData.variants && externalProductData.options) {
            const processedVariants = externalProductData.variants.map((variant) => ({
                id: variant.id.toString(),
                sku: variant.sku || '',
                price: variant.price / 100,
                displayTag: variant.name ?? variant.title,
                available: variant.available,
                options: variant.options,
                featured_image: variant.featured_image,
                featured_media: variant.featured_media,
            }));
            // Convert External Product options to our VariantGroup format
            const processedVariantGroups = externalProductData.options.map((option) => ({
                name: option.name,
                type: option.name.toLowerCase(),
                options: option.values.map((value, index) => {
                    // Check if this option is available in any variant
                    const isAvailable = externalProductData.variants.some((variant) => variant.options.includes(value) && variant.available);
                    return {
                        id: `${option.name}-${index}`,
                        value,
                        available: isAvailable,
                    };
                }),
                required: true,
            }));
            setNormalizedVariants(processedVariants);
            setDynamicVariantGroups(processedVariantGroups);
        }
    };
    // Initialize with direct data if available, then fetch from API
    // Only execute when dual mode is enabled to maintain retrocompatibility
    useEffect(() => {
        if (!isDualModeEnabled) {
            return; // Skip this effect when dual mode is OFF
        }
        // Set initial data from props immediately if available
        if (title && image) {
            const initialData = {
                title: title || '',
                description: description || '',
                image: image || '',
                url: url || '',
                price: price || 0,
                compare_at_price: null,
                currentPrice: price || null,
                originalPrice: null,
            };
            setProductData(initialData);
            setIsLoadingProduct(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDualModeEnabled]); // Only run once on mount, but depend on isDualModeEnabled
    // Prefer productPayload from search item references (Bedrock); else fetch via parent/getProductData.
    useEffect(() => {
        const applyProductJson = (jsonData) => {
            if (!jsonData) {
                const directData = {
                    title: title || '',
                    description: description || '',
                    image: image || '',
                    url: url || '',
                    price: price || 0,
                    compare_at_price: null,
                    currentPrice: price || null,
                    originalPrice: null,
                };
                setProductData(directData);
                return;
            }
            let apiImage = jsonData?.image;
            if (jsonData?.featured_image) {
                apiImage = jsonData.featured_image.startsWith('//')
                    ? `https:${jsonData.featured_image}`
                    : jsonData.featured_image;
            }
            else if (jsonData?.images?.[0]) {
                const img0 = jsonData.images[0];
                apiImage = typeof img0 === 'string'
                    ? (img0.startsWith('//') ? `https:${img0}` : img0)
                    : (img0?.url || img0);
            }
            const priceCents = (jsonData.price_min ?? jsonData.price);
            const fromPayload = priceCents != null && priceCents > 0 ? priceCents / 100 : null;
            const currentPrice = fromPayload ?? (price != null && price > 0 ? price : null);
            const enhancedData = {
                ...(jsonData || {}),
                image: apiImage || image,
                rating: undefined,
                reviewCount: undefined,
                originalPrice: jsonData?.compare_at_price ? jsonData.compare_at_price / 100 : null,
                currentPrice,
                description: jsonData?.description || description || '',
                title: jsonData?.title || title,
            };
            setProductData(enhancedData);
            if (jsonData && (jsonData.variants || jsonData.options)) {
                updateVariantData(enhancedData);
            }
            if (apiImage && apiImage !== image) {
                handleVariantImageChange(apiImage, {
                    action: 'initial',
                    source: 'api_data',
                });
            }
        };
        const preloaded = product?.productPayload;
        if (preloaded && typeof preloaded === 'object') {
            applyProductJson(preloaded);
            setIsLoadingProduct(false);
            return;
        }
        const getData = async () => {
            try {
                setIsLoadingProduct(true);
                const jsonData = await getProductData(url);
                applyProductJson(jsonData);
            }
            catch (error) {
                console.error('[EnhancedProductCard] Error getting product data:', error);
                const directData = {
                    title,
                    description: description || '',
                    image,
                    url,
                    price: price || 0,
                    compare_at_price: null,
                    currentPrice: price || null,
                    originalPrice: null,
                };
                setProductData(directData);
            }
            finally {
                setIsLoadingProduct(false);
            }
        };
        getData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, product?.productPayload]);
    useEffect(() => {
        store.currentConversation?.sendTrackingEvent('product_view', {
            product_name: title,
            product_url: url,
        });
    }, [title, url, store.currentConversation]);
    const resolvedClientAIConfig = store?.clientAIConfig;
    const resolvedProductFormatting = useMemo(() => {
        const rawConfig = resolvedClientAIConfig?.productCardConfig;
        const adminFormatting = rawConfig
            ? mapProductCardConfigToFormatting(rawConfig)
            : undefined;
        const base = {
            template: 'custom',
            orientation: 'vertical',
            showBox: true,
            showImage: true,
            showTitle: true,
            showDescription: true,
            showPrice: true,
            backgroundColor: '#fff',
            addToCart: true,
            // Enhanced defaults
            showRatings: false,
            showOriginalPrice: true,
            showVariantSelection: true,
            showAddToCartButton: true,
            elementOrder: ['productName', 'description', 'pricing', 'variantSelection', 'quantity', 'addToCartButton'],
            titleMaxLines: 2,
            descriptionMaxLines: 3,
            descriptionShowSeeMore: true,
            imageClickable: true,
        };
        // When dual mode is OFF, maintain original merge order:
        // productFormatting first, then adminFormatting
        // When dual mode is ON, productFormatting has priority (new behavior)
        const merged = isDualModeEnabled
            ? {
                ...base,
                ...(adminFormatting || {}),
                ...(productFormatting || {}), // productFormatting has priority (dual mode)
                borderStyle: {
                    borderWidth: 1,
                    borderColor: '#ccc',
                    borderRadius: 4,
                    ...(productFormatting?.borderStyle || {}),
                    ...(adminFormatting?.borderStyle || {}),
                },
            }
            : {
                ...base,
                ...(productFormatting || {}), // Original order: productFormatting first
                ...(adminFormatting || {}),
                borderStyle: {
                    borderWidth: 1,
                    borderColor: '#ccc',
                    borderRadius: 4,
                    ...(productFormatting?.borderStyle || {}),
                    ...(adminFormatting?.borderStyle || {}),
                },
            };
        return merged;
    }, [isDualModeEnabled, productFormatting, resolvedClientAIConfig?.productCardConfig]);
    // Resolve orientation with priority:
    // 1. forceVertical (prop) > 2. forceVertical (responsive) >
    // 3. productFormatting.orientation > 4. responsive finalOrientation
    // When dual mode is OFF, use finalOrientation from hook to respect breakpoints
    const isHorizontal = useMemo(() => {
        // 1. If forceVertical prop is explicitly set (highest priority), use it
        if (forceVertical !== undefined) {
            return !forceVertical;
        }
        // 2. If responsive hook calculated forceVertical (screen too narrow), use it
        if (responsiveForceVertical === true) {
            return false;
        }
        if (!isDualModeEnabled) {
            // Original behavior: Use finalOrientation from hook to respect breakpoints
            // This ensures horizontal cards switch to vertical when screen is too narrow
            return responsiveFinalOrientation === 'horizontal';
        }
        // Dual mode behavior: productFormatting.orientation > responsive finalOrientation
        // 3. If productFormatting.orientation is set, use it
        if (resolvedProductFormatting?.orientation === 'vertical') {
            return false;
        }
        if (resolvedProductFormatting?.orientation === 'horizontal') {
            return true;
        }
        // 4. Fallback to responsive finalOrientation from hook
        return responsiveFinalOrientation === 'horizontal';
    }, [
        isDualModeEnabled,
        forceVertical,
        responsiveForceVertical,
        resolvedProductFormatting?.orientation,
        responsiveFinalOrientation,
    ]);
    // Get stylesheet from conversation store for color resolution
    const { stylesheet } = store;
    // Resolve all configurations using resolveConfig helper (props > aiSearchConfig > defaults)
    // When dual mode is OFF, use original behavior: direct access to aiSearchConfig with defaults
    const maxHeight = isDualModeEnabled
        ? resolveConfig(resolvedProductFormatting?.maxHeight, aiSearchConfig?.mainImage?.maxHeight, 250, isDualModeEnabled)
        : (resolvedProductFormatting?.maxHeight || 250);
    const objectFit = isDualModeEnabled
        ? resolveConfig(resolvedProductFormatting?.objectFit, aiSearchConfig?.mainImage?.objectFit, 'contain', isDualModeEnabled)
        : (resolvedProductFormatting?.objectFit || 'contain');
    const isCoverFullHeight = objectFit === 'cover-full-height';
    const isCoverFullHeightEnabled = isDualModeEnabled && isCoverFullHeight;
    const isCoverFullHeightHorizontal = isCoverFullHeightEnabled && isHorizontal;
    const isCoverFullHeightVertical = isCoverFullHeightEnabled && !isHorizontal;
    // For cover-full-height, always render without crop (contain).
    const resolvedObjectFit = isCoverFullHeightEnabled ? 'contain' : objectFit;
    const titleMaxLines = isDualModeEnabled
        ? resolveConfig(resolvedProductFormatting?.titleMaxLines, aiSearchConfig?.typography?.cardTitleMaxLines, 2, isDualModeEnabled)
        : (resolvedProductFormatting?.titleMaxLines || 2);
    // Always use portal config for max lines
    // (cardMaxLines in aiSearchConfig is character length, not line count)
    const descriptionMaxLines = resolvedProductFormatting?.descriptionMaxLines ?? 3;
    const descriptionMaxLength = isDualModeEnabled
        ? resolveConfig(resolvedProductFormatting?.descriptionMaxLength, aiSearchConfig?.description?.cardMaxLength, 9999, isDualModeEnabled)
        : (resolvedProductFormatting?.descriptionMaxLength
            ?? productFormatting?.descriptionMaxLength
            ?? 9999);
    const cardPadding = isDualModeEnabled
        ? resolveConfig(resolvedProductFormatting?.padding, aiSearchConfig?.card?.padding, 1, isDualModeEnabled)
        : 1;
    const stackSpacing = isDualModeEnabled
        ? resolveConfig(resolvedProductFormatting?.spacing, aiSearchConfig?.card?.spacing, 0.5, isDualModeEnabled)
        : 0.5;
    const { customizable } = product || {};
    const personalizationConfig = aiSearchConfig?.personalization
        || resolvedClientAIConfig?.aiSearchConfig?.personalization;
    // Use new complete validation logic instead of simple ID validation
    const buttonState = useMemo(() => {
        // Build button texts from admin configuration, with token fallbacks
        const buttonTexts = {
            addToBag: resolvedProductFormatting?.buttonText || tokens.getValue('addToBagLabel'),
            addedToCart: resolvedProductFormatting?.addedToCartText || tokens.getValue('addedToCartLabel'),
            loading: resolvedProductFormatting?.loadingText || tokens.getValue('addingLabel'),
            soldOut: resolvedProductFormatting?.soldOutText || tokens.getValue('soldOutLabel'),
            selectAllOptions: resolvedProductFormatting?.selectAllOptionsText || tokens.getValue('selectAllOptionsLabel'),
        };
        const baseState = getButtonState(selectedVariantOptions, productData?.options || [], normalizedVariants, isAddingToCart, isAddedToCart, buttonTexts);
        // If we have zero variant/option data after load, force SOLD OUT to avoid "select options"
        const hasVariantData = normalizedVariants.length > 0
            || (productData?.variants?.length || 0) > 0;
        const hasOptionData = (productData?.options?.length || 0) > 0;
        const missingVariantData = !isLoadingProduct && !hasVariantData && !hasOptionData;
        const totalInventory = productData?.totalInventory
            || productData?.total_inventory;
        const productLevelSoldOut = productData?.available === false
            || product?.available === false
            || (typeof totalInventory === 'number' && totalInventory <= 0);
        if (missingVariantData || productLevelSoldOut) {
            const soldOutText = buttonTexts.soldOut || tokens.getValue('soldOutLabel');
            return {
                ...baseState,
                text: soldOutText,
                disabled: true,
                showCheckout: false,
                showSuccessMessage: false,
                isSoldOut: true,
                isLoading: false,
            };
        }
        return baseState;
    }, [
        selectedVariantOptions,
        normalizedVariants,
        isAddingToCart,
        isAddedToCart,
        resolvedProductFormatting,
        isLoadingProduct,
        productData,
        product,
        tokens,
    ]);
    const isCheckoutFlowVisible = buttonState.showCheckout
        && resolvedProductFormatting?.showCheckoutFlow !== false;
    useEffect(() => {
        if (!isCheckoutFlowVisible)
            return;
        checkoutFlowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [isCheckoutFlowVisible]);
    const resolvedAddToCartText = resolvedProductFormatting?.addedToCartText || tokens.getValue('addedToCartLabel');
    const resolvedLoadingText = resolvedProductFormatting?.loadingText || tokens.getValue('addingLabel');
    const resolvedSoldOutText = resolvedProductFormatting?.soldOutText || tokens.getValue('soldOutLabel');
    const resolvedCheckoutText = resolvedProductFormatting?.checkoutText || tokens.getValue('checkoutLabel');
    const { showBox, showImage, borderStyle = {}, backgroundColor, 
    // showRatings,
    elementOrder, imageClickable, clickBehavior = 'navigate', cursorStyle = 'default', cardInteractive = false, } = resolvedProductFormatting;
    useEffect(() => {
        if (!normalizedInitialVariantId || normalizedVariants.length === 0)
            return;
        const initialVariant = normalizedVariants.find((variant) => normalizeVariantId(variant.id) === normalizedInitialVariantId);
        if (!initialVariant) {
            if (selectedVariantId === normalizedInitialVariantId) {
                setSelectedVariantId('');
            }
            return;
        }
        if (selectedVariantId !== normalizedInitialVariantId) {
            setSelectedVariantId(normalizedInitialVariantId);
        }
        const syncedOptions = {};
        (productData?.options || []).forEach((option, index) => {
            const optionValue = getVariantOptionAtPosition(initialVariant, index);
            if (optionValue) {
                syncedOptions[option.name] = optionValue;
            }
        });
        setSelectedVariantOptions((previousOptions) => (recordsEqual(previousOptions, syncedOptions) ? previousOptions : syncedOptions));
        const bestImage = findBestImageForSelection(syncedOptions, normalizedVariants, productData);
        if (bestImage?.imageUrl) {
            setCurrentImageUrl(bestImage.imageUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        normalizedInitialVariantId,
        normalizedVariants,
        productData,
        selectedVariantId,
        findBestImageForSelection,
    ]);
    // Auto-select single variants using shared utility
    useEffect(() => {
        if (!selectedVariantId && normalizedVariants.length > 0) {
            const hasInitialVariant = normalizedInitialVariantId
                && normalizedVariants.some((variant) => normalizeVariantId(variant.id) === normalizedInitialVariantId);
            if (hasInitialVariant)
                return;
            const autoSelectVariant = shouldAutoSelectVariant(normalizedVariants);
            if (autoSelectVariant) {
                setSelectedVariantId(autoSelectVariant.id?.toString() || '');
                // CRITICAL: Also set selectedVariantOptions for single variants
                if (productData?.options && autoSelectVariant.options) {
                    const syncedOptions = {};
                    productData.options.forEach((option, index) => {
                        if (autoSelectVariant.options[index]) {
                            syncedOptions[option.name] = autoSelectVariant.options[index];
                        }
                    });
                    setSelectedVariantOptions(syncedOptions);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [normalizedVariants, selectedVariantId, normalizedInitialVariantId]);
    const activeVariants = useMemo(() => (normalizedVariants.length > 0 ? normalizedVariants : []), [normalizedVariants]);
    const selectedVariant = useMemo(() => getSelectedVariant(selectedVariantId, selectedVariantOptions, activeVariants, productData?.options || []), [selectedVariantId, selectedVariantOptions, activeVariants, productData?.options]);
    // Use the validation from buttonState for consistency
    const isValidVariantSelected = buttonState.disabled === false;
    const handleAddToCart = async () => {
        if (!isValidVariantSelected || !selectedVariant) {
            return;
        }
        const variantToUse = selectedVariant;
        if (!variantToUse || variantToUse.available === false) {
            return;
        }
        setIsAddingToCart(true);
        try {
            // Include product-level identity alongside variantId so non-Shopify host
            // adapters (e.g. BuyBlvd → "Save to List") can act on the product without
            // needing a variant-level SKU lookup. Shopify ignores these (MCN-2268).
            const productPayloadForMessage = (preloadedPayload ?? productData);
            const response = await createEmbedMessageHandler({
                type: 'addToCart',
                payload: {
                    variantId: variantToUse.id,
                    quantity: Math.max(1, Number.isFinite(quantity) ? Number(quantity) : 1),
                    productId: productPayloadForMessage?.id ?? null,
                    productHandle: productPayloadForMessage?.handle ?? null,
                    title: productData?.title || title,
                    // instanceId,
                },
                timeout: 10000,
            });
            store.currentConversation?.sendTrackingEvent('addToCart', {
                product_name: productData?.title || title,
                variant_id: variantToUse.id,
                variant_sku: variantToUse.sku,
            });
            if (response.success) {
                setIsAddedToCart(true);
            }
            else {
                console.error('[ProductCard] Add to cart failed:', response.error);
            }
        }
        catch (error) {
            console.error('[ProductCard] Add to cart error:', error);
        }
        finally {
            setIsAddingToCart(false);
        }
    };
    // Resolve seeMoreText and seeLessText using tokens
    const resolvedSeeMoreText = useMemo(() => {
        // Priority: productFormatting > aiSearchConfig (if dual mode) > tokens
        const tokenValue = tokens.getValue('seeMoreText');
        if (resolvedProductFormatting?.seeMoreText) {
            return resolvedProductFormatting.seeMoreText;
        }
        if (isDualModeEnabled) {
            const aiSearchValue = aiSearchConfig?.topSection?.seeMoreLabel;
            if (aiSearchValue && aiSearchValue.trim() !== '') {
                return aiSearchValue;
            }
        }
        return tokenValue;
    }, [
        isDualModeEnabled,
        resolvedProductFormatting?.seeMoreText,
        aiSearchConfig?.topSection?.seeMoreLabel,
        tokens,
    ]);
    const resolvedSeeLessText = useMemo(() => {
        // Priority: productFormatting > aiSearchConfig (if dual mode) > tokens
        const tokenValue = tokens.getValue('seeLessText');
        if (resolvedProductFormatting?.seeLessText) {
            return resolvedProductFormatting.seeLessText;
        }
        if (isDualModeEnabled) {
            const aiSearchValue = aiSearchConfig?.topSection?.seeLessLabel;
            if (aiSearchValue && aiSearchValue.trim() !== '') {
                return aiSearchValue;
            }
        }
        return tokenValue;
    }, [
        isDualModeEnabled,
        resolvedProductFormatting?.seeLessText,
        aiSearchConfig?.topSection?.seeLessLabel,
        tokens,
    ]);
    // Resolve final title and description
    // When dual mode is OFF, use original behavior:
    // title from props, description from productData or props
    // If preferShopifyTitle is true, always use productData?.title
    const finalTitle = useMemo(() => {
        if (resolvedProductFormatting?.preferShopifyTitle === true) {
            return productData?.title || title || '';
        }
        if (isDualModeEnabled) {
            return productData?.title || title || '';
        }
        return title || '';
    }, [
        resolvedProductFormatting?.preferShopifyTitle,
        productData?.title,
        title,
        isDualModeEnabled,
    ]);
    const finalDescription = isDualModeEnabled
        ? (productData?.description || description || '')
        : (productData?.description || description || '');
    // Calculate maxHeight as number for responsive calculations
    // When dual mode is OFF, skip compact mode logic
    let maxHeightNum;
    let isCompactMode = false;
    let hasConstrainedHeight = false;
    let responsiveTitleFontSize;
    let responsivePriceFontSize;
    if (isDualModeEnabled) {
        // Dual mode: Calculate responsive adjustments based on maxHeight
        if (typeof maxHeight === 'number') {
            maxHeightNum = maxHeight;
        }
        else if (typeof maxHeight === 'string') {
            maxHeightNum = parseFloat(maxHeight);
        }
        // Compact mode for very small cards (<= 150px), constrained height for any maxHeight
        isCompactMode = maxHeightNum !== undefined && maxHeightNum <= 150;
        hasConstrainedHeight = maxHeightNum !== undefined;
        if (isCoverFullHeightHorizontal) {
            maxHeightNum = undefined;
            isCompactMode = false;
            hasConstrainedHeight = false;
        }
        // Calculate responsive font sizes for compact mode
        if (isCompactMode && maxHeightNum) {
            const scaleFactor = Math.max(0.75, maxHeightNum / 250); // Scale between 0.75 and 1.0
            const baseTitleFontSize = resolveConfig(resolvedProductFormatting?.titleFontSize, aiSearchConfig?.typography?.titleFontSize, undefined, isDualModeEnabled);
            if (baseTitleFontSize) {
                if (typeof baseTitleFontSize === 'number') {
                    responsiveTitleFontSize = `${baseTitleFontSize * scaleFactor}px`;
                }
                else if (typeof baseTitleFontSize === 'string' && baseTitleFontSize.endsWith('px')) {
                    const numValue = parseFloat(baseTitleFontSize);
                    responsiveTitleFontSize = `${numValue * scaleFactor}px`;
                }
                else {
                    responsiveTitleFontSize = baseTitleFontSize;
                }
            }
            const basePriceFontSize = aiSearchConfig?.typography?.priceFontSize;
            if (basePriceFontSize) {
                if (typeof basePriceFontSize === 'number') {
                    responsivePriceFontSize = `${basePriceFontSize * scaleFactor}px`;
                }
                else if (typeof basePriceFontSize === 'string' && basePriceFontSize.endsWith('px')) {
                    const numValue = parseFloat(basePriceFontSize);
                    responsivePriceFontSize = `${numValue * scaleFactor}px`;
                }
                else {
                    responsivePriceFontSize = basePriceFontSize;
                }
            }
        }
        else {
            const baseTitleFontSize = resolveConfig(resolvedProductFormatting?.titleFontSize, aiSearchConfig?.typography?.titleFontSize, undefined, isDualModeEnabled);
            responsiveTitleFontSize = typeof baseTitleFontSize === 'number'
                ? `${baseTitleFontSize}px`
                : baseTitleFontSize;
            const basePriceFontSize = aiSearchConfig?.typography?.priceFontSize;
            responsivePriceFontSize = typeof basePriceFontSize === 'number'
                ? `${basePriceFontSize}px`
                : basePriceFontSize;
        }
    }
    else {
        // Original behavior: Use aiSearchConfig directly
        responsiveTitleFontSize = aiSearchConfig?.typography?.titleFontSize;
        responsivePriceFontSize = aiSearchConfig?.typography?.priceFontSize;
    }
    const handleTitleClick = () => {
        // When dual mode is OFF, use original behavior: always call onProductClick if it exists
        if (!isDualModeEnabled) {
            if (onProductClick && product) {
                onProductClick(product);
            }
            else if (url) {
                window.open(url, '_top');
            }
            return;
        }
        // Dual mode behavior: check clickBehavior first
        if (clickBehavior === 'callback' && onProductClick && product) {
            onProductClick(product);
        }
        else if (url) {
            window.open(url, '_top');
        }
    };
    // Extract original values for retrocompatibility
    const originalDescriptionShowSeeMore = resolvedProductFormatting?.descriptionShowSeeMore ?? true;
    const isAddToCartVisible = Boolean(resolvedClientAIConfig?.addToCartEnabled
        && resolvedProductFormatting?.addToCart !== false
        && resolvedProductFormatting?.showAddToCartButton !== false);
    const shouldShowCTA = !isAddToCartVisible
        && resolvedProductFormatting?.showCTAButton === true
        && Boolean(url);
    const ctaLabel = tokens.getValue('viewDetailsLabel');
    const distanceMiles = useMemo(() => {
        const geo = store?.customerContext?.geoLocation;
        if (!geo || typeof geo.lat !== 'number' || typeof geo.lng !== 'number')
            return null;
        const locations = product?.productPayload?.locations;
        return nearestLocationDistanceMiles(geo.lat, geo.lng, locations);
    }, [store?.customerContext?.geoLocation, product?.productPayload]);
    const elements = {
        productName: (_jsx(ProductTitle, { title: finalTitle, variant: isDualModeEnabled
                ? resolveConfig(resolvedProductFormatting?.titleVariant, aiSearchConfig?.typography?.cardTitleVariant, 'h6', isDualModeEnabled)
                : (aiSearchConfig?.typography?.cardTitleVariant || 'h6'), maxLines: titleMaxLines, fontSize: responsiveTitleFontSize, sx: { flexShrink: 0 }, onClick: (() => {
                if (!isDualModeEnabled) {
                    // Original behavior: always call onProductClick if it exists
                    if (onProductClick && product) {
                        return () => onProductClick(product);
                    }
                    if (url) {
                        return () => window.open(url, '_top');
                    }
                    return undefined;
                }
                // Dual mode behavior: use handleTitleClick
                return url ? handleTitleClick : undefined;
            })(), isLoading: isLoadingProduct })),
        ratings: (_jsx(Ratings, {})),
        description: (_jsx(Box, { children: _jsx(DescriptionRenderer, { description: finalDescription, maxLines: descriptionMaxLines, maxLength: descriptionMaxLength, showSeeMore: isDualModeEnabled
                    ? resolveConfig(resolvedProductFormatting?.descriptionShowSeeMore, aiSearchConfig?.description?.showSeeMore, true, isDualModeEnabled)
                    : (aiSearchConfig?.description?.showSeeMore ?? originalDescriptionShowSeeMore), showMoreText: resolvedSeeMoreText, showLessText: resolvedSeeLessText, stylesheet: stylesheet, isLoading: isLoadingProduct }) })),
        pricing: (_jsx(Box, { "data-card-slot": "pricing", sx: {
                lineHeight: 1,
                mt: 0,
                mb: 0,
            }, children: _jsx(ProductPrice, { price: productData?.price || price, variants: normalizedVariants, selectedVariant: (() => {
                    const result = getSelectedVariant(selectedVariantId, selectedVariantOptions, normalizedVariants, productData?.options || []);
                    return result;
                })(), variant: "body1", fontSize: responsivePriceFontSize, locale: locale, currency: currency, isLoading: isLoadingProduct }) })),
        variantSelection: (dynamicVariantGroups.length > 0 && normalizedVariants.length > 1 ? (_jsx(VariantSelector, { variantGroups: dynamicVariantGroups, variants: normalizedVariants, dataSource: { options: productData?.options || [], variants: normalizedVariants }, aiSearchConfig: aiSearchConfig, selectedVariantOptions: selectedVariantOptions, imageSwatchesConfig: aiSearchConfig?.variantPhotoSwatches, stylesheet: stylesheet, onVariantSelectionChange: (selection, variantId) => {
                setSelectedVariantOptions(selection);
                // Use shared utility to ensure state synchronization
                if (variantId) {
                    setSelectedVariantId(variantId);
                }
                else {
                    const syncedStates = syncVariantStates(null, selection, normalizedVariants, productData?.options || []);
                    setSelectedVariantId(syncedStates.syncedVariantId || '');
                }
                // Update main image when variant selection changes
                const bestImage = findBestImageForSelection(selection, normalizedVariants, productData);
                if (bestImage && bestImage.imageUrl !== currentImageUrl) {
                    setCurrentImageUrl(bestImage.imageUrl);
                }
                setHoverImageUrl('');
            }, onOptionHover: isMobileView ? undefined : handleOptionHover, onOptionLeave: isMobileView ? undefined : handleOptionLeave, disableTooltip: isMobileView, spacing: 0.5, enableImageSwatching: aiSearchConfig?.variantPhotoSwatches?.enabled ?? true, isLoading: isLoadingProduct })) : null),
        quantity: (isAddToCartVisible && resolvedProductFormatting?.showQuantitySelector !== false ? (_jsx(Box, { children: _jsx(QuantityField, { quantity: quantity, setQuantity: setQuantity, disabled: isAddingToCart, minQuantity: 1, maxQuantity: 10 }) })) : null),
        addToCartButton: (_jsx(Box, { "data-card-slot": "actions", sx: { mt: '8px', pb: 0 }, children: isAddToCartVisible ? (_jsxs(_Fragment, { children: [customizable && personalizationConfig?.enabled ? (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [_jsx(Button, { variant: "outlined", onClick: () => window.open(url, '_top'), sx: {
                                    width: '100%',
                                    backgroundColor: 'transparent',
                                    color: replaceStylesheetGlobals(stylesheet, resolvedProductFormatting?.buttonStyle?.backgroundColor || 'color_primary') || '#2563eb',
                                    border: `1px solid ${replaceStylesheetGlobals(stylesheet, resolvedProductFormatting?.buttonStyle?.backgroundColor || 'color_primary') || '#2563eb'}`,
                                    borderRadius: resolvedProductFormatting?.buttonStyle?.borderRadius || 4,
                                    '&:hover': {
                                        backgroundColor: replaceStylesheetGlobals(stylesheet, 'color_background') || '#f9fafb',
                                        borderColor: replaceStylesheetGlobals(stylesheet, resolvedProductFormatting?.buttonStyle?.backgroundColor || 'color_primary') || '#2563eb',
                                        color: replaceStylesheetGlobals(stylesheet, resolvedProductFormatting?.buttonStyle?.hoverColor || 'color_font') || '#1f2937',
                                    },
                                }, children: personalizationConfig?.personalizeButtonText || 'Click to Personalize' }), _jsx(AddToCartButton, { onAddToCart: handleAddToCart, disabled: buttonState.disabled, loading: isAddingToCart, soldOut: buttonState.isSoldOut, isAddedToCart: isAddedToCart, buttonText: buttonState.text, addedToCartText: resolvedAddToCartText, soldOutText: resolvedSoldOutText, loadingText: resolvedLoadingText, buttonStyle: resolvedProductFormatting?.buttonStyle, stylesheet: stylesheet })] })) : (_jsx(AddToCartButton, { onAddToCart: handleAddToCart, disabled: buttonState.disabled, loading: isAddingToCart, soldOut: buttonState.isSoldOut, isAddedToCart: isAddedToCart, buttonText: buttonState.text, addedToCartText: resolvedAddToCartText, soldOutText: resolvedSoldOutText, loadingText: resolvedLoadingText, buttonStyle: resolvedProductFormatting?.buttonStyle, stylesheet: stylesheet })), _jsx("div", { ref: checkoutFlowRef, children: _jsx(CheckoutFlow, { isVisible: buttonState.showCheckout
                                && resolvedProductFormatting?.showCheckoutFlow !== false, productTitle: productData?.title || title, checkoutText: resolvedCheckoutText, onCheckout: () => {
                                window.parent.postMessage({
                                    source: 'microcommerce-ai-embed',
                                    type: 'checkout',
                                    checkoutUrl: resolvedProductFormatting?.checkoutUrl || '/checkout',
                                }, '*');
                            }, containerSX: { mt: 1, p: 1, borderRadius: 1 }, buttonSX: { fontSize: '0.85rem', py: 1 }, stylesheet: stylesheet, buttonStyle: resolvedProductFormatting?.buttonStyle }) })] })) : (shouldShowCTA && (_jsx(Button, { component: "a", href: url, target: "_top", rel: "noreferrer", variant: "contained", sx: {
                    width: '100%',
                    backgroundColor: resolvedProductFormatting?.buttonStyle?.backgroundColor
                        ? replaceStylesheetGlobals(stylesheet, resolvedProductFormatting?.buttonStyle?.backgroundColor)
                            || resolvedProductFormatting?.buttonStyle?.backgroundColor
                        : undefined,
                    color: resolvedProductFormatting?.buttonStyle?.color
                        ? replaceStylesheetGlobals(stylesheet, resolvedProductFormatting?.buttonStyle?.color)
                            || resolvedProductFormatting?.buttonStyle?.color
                        : undefined,
                    borderRadius: resolvedProductFormatting?.buttonStyle?.borderRadius || 4,
                    '&:hover': {
                        backgroundColor: resolvedProductFormatting?.buttonStyle?.hoverBackgroundColor
                            ? replaceStylesheetGlobals(stylesheet, resolvedProductFormatting?.buttonStyle?.hoverBackgroundColor)
                                || resolvedProductFormatting?.buttonStyle?.hoverBackgroundColor
                            : undefined,
                    },
                    '&:disabled': {
                        backgroundColor: resolvedProductFormatting?.buttonStyle?.disabledBackgroundColor
                            ? replaceStylesheetGlobals(stylesheet, resolvedProductFormatting?.buttonStyle?.disabledBackgroundColor)
                                || resolvedProductFormatting?.buttonStyle?.disabledBackgroundColor
                            : undefined,
                        color: resolvedProductFormatting?.buttonStyle?.disabledColor
                            ? replaceStylesheetGlobals(stylesheet, resolvedProductFormatting?.buttonStyle?.disabledColor)
                                || resolvedProductFormatting?.buttonStyle?.disabledColor
                            : undefined,
                    },
                }, children: ctaLabel }))) })),
        distance: distanceMiles != null ? (
        // `color: 'inherit'` + opacity keeps this readable on any card theme —
        // `text.secondary` resolved to white on portals with dark-palette MUI themes.
        _jsx(Box, { sx: { fontSize: '0.875rem', color: 'inherit', opacity: 0.7 }, children: `Distance ${distanceMiles.toFixed(1)} mi` })) : null,
        footerSpacer: (_jsx(Box, { "aria-hidden": true, sx: { flex: 1, minHeight: 0, width: '100%' } })),
    };
    // Reset cart state when variant or quantity changes
    useEffect(() => {
        if (isAddedToCart) {
            setIsAddedToCart(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVariantId, selectedVariantOptions, quantity]);
    const allowedElements = useMemo(() => {
        const base = Array.isArray(elementOrder) ? elementOrder.slice() : [];
        const filtered = base.filter((elementType) => {
            let shouldShow;
            switch (elementType) {
                case 'addToCartButton':
                    return isAddToCartVisible || shouldShowCTA;
                case 'quantity':
                    return isAddToCartVisible
                        && resolvedProductFormatting?.showQuantitySelector !== false;
                case 'variantSelection':
                    shouldShow = resolvedProductFormatting?.showVariantSelection !== false;
                    return shouldShow;
                case 'productName':
                    return resolvedProductFormatting?.showTitle !== false;
                case 'description':
                    return resolvedProductFormatting?.showDescription !== false;
                case 'pricing':
                    return resolvedProductFormatting?.showPrice !== false;
                case 'ratings':
                    return false; // resolvedProductFormatting?.showRatings === true; // opt-in only
                case 'distance':
                    return resolvedProductFormatting?.showDistance === true && distanceMiles != null;
                case 'footerSpacer':
                    return false;
                default:
                    return true; // Unknown elements are shown by default
            }
        });
        // Backward compatibility: older elementOrder configs group quantity inside
        // addToCartButton. Surface it as its own element (just before the button) so
        // it stays with the options and only the button pins to the card bottom.
        const showQuantity = isAddToCartVisible
            && resolvedProductFormatting?.showQuantitySelector !== false;
        if (showQuantity && !filtered.includes('quantity')) {
            const buttonIndex = filtered.indexOf('addToCartButton');
            if (buttonIndex >= 0) {
                filtered.splice(buttonIndex, 0, 'quantity');
            }
            else {
                filtered.push('quantity');
            }
        }
        const firstFooterIndex = filtered.findIndex((elementType) => elementType === 'quantity' || elementType === 'addToCartButton');
        if (firstFooterIndex >= 0) {
            filtered.splice(firstFooterIndex, 0, 'footerSpacer');
        }
        if (filtered.length === 0) {
            // When dual mode is OFF, use original behavior: always return default elements
            if (!isDualModeEnabled) {
                return ['productName', 'pricing', 'description'];
            }
            // Dual mode behavior: only include elements that should be shown
            const fallback = [];
            if (resolvedProductFormatting?.showTitle !== false) {
                fallback.push('productName');
            }
            if (resolvedProductFormatting?.showPrice !== false) {
                fallback.push('pricing');
            }
            if (resolvedProductFormatting?.showDescription !== false) {
                fallback.push('description');
            }
            return fallback.length > 0 ? fallback : ['productName'];
        }
        return filtered;
    }, [
        isDualModeEnabled,
        elementOrder,
        resolvedProductFormatting,
        isAddToCartVisible,
        shouldShowCTA,
        distanceMiles,
    ]);
    const handleCardClick = (e) => {
        e.preventDefault();
        store.currentConversation?.sendTrackingEvent('product_click', {
            product_name: title,
            product_url: url,
        });
        if (clickBehavior === 'callback' && onProductClick && product) {
            onProductClick(product);
        }
        else if (clickBehavior === 'navigate') {
            window.open(url, '_top');
        }
        // clickBehavior === 'none' does nothing
    };
    let originalImageMaxHeight;
    if (isHorizontal) {
        originalImageMaxHeight = undefined;
    }
    else {
        originalImageMaxHeight = maxHeight ? `${maxHeight}px` : 'auto';
    }
    return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    _jsx("div", { style: {
            textDecoration: 'none',
            color: 'inherit',
            position: 'relative',
            paddingBottom: 0,
            cursor: cursorStyle,
        }, onClick: cardInteractive ? handleCardClick : undefined, onKeyDown: cardInteractive ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCardClick(e);
            }
        } : undefined, role: cardInteractive ? 'button' : undefined, 
        // eslint-disable-next-line jsx-a11y/tabindex-no-positive, jsx-a11y/no-noninteractive-tabindex
        tabIndex: cardInteractive ? 0 : undefined, children: isDualModeEnabled ? (() => {
            // DUAL MODE: Complex rendering with responsive calculations
            // Horizontal: no maxHeight on card - let it grow with content (image+text)
            // Vertical: apply maxHeight for compact cards
            const cardMaxHeight = maxHeightNum && !isHorizontal
                ? `${maxHeightNum}px`
                : undefined;
            let finalGridTemplateColumns = '1fr';
            if (!isHorizontal && showImage) {
                finalGridTemplateColumns = '1fr';
            }
            else if (isHorizontal && showImage) {
                finalGridTemplateColumns = '50% 50%';
            }
            // Use isCompactMode already calculated above
            const hasDescription = resolvedProductFormatting?.showDescription !== false;
            const hasPrice = resolvedProductFormatting?.showPrice !== false;
            // Calculate responsive padding and spacing based on maxHeight
            // (calculated first, before imageMaxHeight)
            let responsivePadding = cardPadding;
            let responsiveSpacing = stackSpacing;
            if (isCompactMode && maxHeightNum) {
                // Scale down padding and spacing proportionally with better minimums
                const scaleFactor = Math.max(0.4, maxHeightNum / 250); // Scale between 0.4 and 1.0
                responsivePadding = typeof cardPadding === 'number'
                    ? cardPadding * scaleFactor
                    : cardPadding;
                responsiveSpacing = stackSpacing * scaleFactor;
            }
            // Calculate image and content proportions dynamically
            // Allow override via productFormatting, otherwise use defaults
            const imageRowRatioConfig = resolvedProductFormatting?.imageRowRatio;
            const contentRowRatioConfig = resolvedProductFormatting?.contentRowRatio;
            // If no description/price, give more space to image
            let imageRowRatio = imageRowRatioConfig || '60%';
            let contentRowRatio = contentRowRatioConfig || '40%';
            if ((hasDescription || hasPrice) && !imageRowRatioConfig) {
                // When there's content, split more evenly but favor image slightly
                imageRowRatio = '55%';
                contentRowRatio = '45%';
            }
            // Use gridTemplateRows when there's constrained height and vertical orientation
            let gridTemplateRows;
            if (hasConstrainedHeight && !isHorizontal && showImage) {
                if (isCoverFullHeightVertical) {
                    gridTemplateRows = 'minmax(0, 1fr) auto';
                }
                else {
                    gridTemplateRows = `${imageRowRatio} ${contentRowRatio}`;
                }
            }
            // Calculate responsive image maxHeight
            // When there's constrained height, calculate as proportion of available space
            let imageMaxHeight;
            if (isCoverFullHeightVertical && hasConstrainedHeight && maxHeightNum && !isHorizontal) {
                imageMaxHeight = '100%';
            }
            else if (hasConstrainedHeight && maxHeightNum && !isHorizontal) {
                // Allow override of imageHeightRatio via productFormatting
                const imageHeightRatioConfig = resolvedProductFormatting?.imageHeightRatio;
                const imageHeightRatio = imageHeightRatioConfig
                    ?? (hasDescription || hasPrice ? 0.55 : 0.6);
                // Subtract padding from calculation to ensure image fits properly
                const paddingTotal = typeof responsivePadding === 'number'
                    ? responsivePadding * 2 // top + bottom
                    : 0;
                const availableHeight = maxHeightNum - paddingTotal;
                const calculatedImageHeight = availableHeight * imageHeightRatio;
                imageMaxHeight = `${calculatedImageHeight}px`;
            }
            else if (isCoverFullHeightHorizontal) {
                imageMaxHeight = 'auto';
            }
            else {
                // Resolve image maxHeight using resolveConfig for normal mode
                const imageMaxHeightValue = resolveConfig(resolvedProductFormatting?.imageMaxHeight, aiSearchConfig?.mainImage?.maxHeight, maxHeight, isDualModeEnabled);
                if (imageMaxHeightValue) {
                    imageMaxHeight = typeof imageMaxHeightValue === 'number'
                        ? `${imageMaxHeightValue}px`
                        : imageMaxHeightValue;
                }
                else {
                    imageMaxHeight = 'auto';
                }
            }
            // Extract width-related styles and maxHeight from customStyle
            // When horizontal, never apply maxHeight (avoids 250px default breaking layout)
            const customStyleObj = customStyle || {};
            const { gridTemplateColumns: unusedGridTemplateColumns, width: customStyleWidth, maxWidth: customStyleMaxWidth, maxHeight: customStyleMaxHeight, ...customStyleWithoutWidth } = customStyleObj;
            // Resolve width with priority:
            // customStyle.width > aiSearchConfig.card.width > cardWidth from hook
            const resolvedWidth = resolveConfig(customStyleWidth, aiSearchConfig?.card?.width, cardWidth, isDualModeEnabled);
            const finalWidth = typeof resolvedWidth === 'string'
                ? resolvedWidth
                : `${resolvedWidth}px`;
            // Resolve maxWidth with priority:
            // customStyle.maxWidth > aiSearchConfig.card.maxWidth > resolvedWidth
            const resolvedMaxWidth = resolveConfig(customStyleMaxWidth, aiSearchConfig?.card?.maxWidth, resolvedWidth, isDualModeEnabled);
            const finalMaxWidth = typeof resolvedMaxWidth === 'string'
                ? resolvedMaxWidth
                : `${resolvedMaxWidth}px`;
            // Use responsive font sizes already calculated above
            // Apply height when there's constrained height and gridTemplateRows is used
            const shouldApplyHeight = gridTemplateRows && hasConstrainedHeight && maxHeightNum;
            // When horizontal: never apply maxHeight. When vertical: customStyle > cardMaxHeight
            const finalCardMaxHeight = isHorizontal
                ? undefined
                : (customStyleMaxHeight ?? cardMaxHeight);
            const cardSx = {
                display: 'grid',
                maxHeight: finalCardMaxHeight,
                // Add height when constrained height and gridTemplateRows are used (vertical only)
                ...(shouldApplyHeight && !isHorizontal && { height: finalCardMaxHeight }),
                ...customStyleWithoutWidth,
                // Apply width after customStyleWithoutWidth to ensure it has priority
                width: finalWidth,
                maxWidth: finalMaxWidth,
                minWidth: customStyleWidth || undefined,
                color: replaceStylesheetGlobals(stylesheet, 'color_font') || 'inherit',
                gridTemplateColumns: finalGridTemplateColumns,
                ...(gridTemplateRows && { gridTemplateRows }),
                gap: 0,
                overflow: 'hidden',
                boxShadow: showBox ? '0px 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
                border: showBox
                    ? `${borderStyle.borderWidth || 0}px solid ${replaceStylesheetGlobals(stylesheet, borderStyle.borderColor || '') || borderStyle.borderColor || ''}`
                    : 'none',
                borderRadius: borderStyle.borderRadius || 0,
                backgroundColor: replaceStylesheetGlobals(stylesheet, backgroundColor)
                    || backgroundColor,
                position: 'relative',
                ...((shouldApplyResponsiveStyles || isTinyScreen) && {
                    margin: '0 auto',
                }),
            };
            return (_jsxs(Card, { sx: cardSx, children: [showImage && (_jsx(CardImage, { product: {
                            ...product,
                            image: (() => {
                                const finalImage = hoverImageUrl || currentImageUrl || mainImageUrl || image;
                                return finalImage;
                            })(),
                            title,
                        }, onClick: () => {
                            if (onProductClick && product && imageClickable) {
                                onProductClick(product);
                            }
                            else if (imageClickable) {
                                store.currentConversation?.sendTrackingEvent('product_click', {
                                    product_name: title,
                                    product_url: url,
                                });
                                window.open(url, '_top');
                            }
                        }, imageClickable: imageClickable, borderStyle: borderStyle, isHorizontal: isHorizontal, maxHeight: isHorizontal ? undefined : imageMaxHeight, objectFit: resolvedObjectFit })), _jsx(CardContent, { sx: {
                            px: responsivePadding,
                            pt: responsivePadding,
                            pb: responsivePadding,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0,
                            '&:last-child': {
                                paddingBottom: responsivePadding,
                            },
                        }, children: _jsx(Stack, { direction: "column", spacing: responsiveSpacing, sx: { minHeight: 0, flex: 1, width: '100%' }, children: allowedElements?.map((elementType) => elements[elementType]) }) })] }));
        })() : (
        // ORIGINAL RENDERING: Simple rendering when dual mode is OFF
        _jsxs(Card, { sx: {
                ...(customStyle || {}),
                width: cardWidth,
                color: replaceStylesheetGlobals(stylesheet, 'color_font') || 'inherit',
                ...(isHorizontal && showImage ? {
                    display: 'grid',
                    gridTemplateColumns: { xs: '50% 50%' },
                } : {
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                }),
                gap: 0,
                overflow: 'hidden',
                boxShadow: showBox ? '0px 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
                border: showBox
                    ? `${borderStyle.borderWidth || 0}px solid ${replaceStylesheetGlobals(stylesheet, borderStyle.borderColor || '') || borderStyle.borderColor || ''}`
                    : 'none',
                borderRadius: borderStyle.borderRadius || 0,
                backgroundColor: replaceStylesheetGlobals(stylesheet, backgroundColor)
                    || backgroundColor,
                position: 'relative',
                ...((shouldApplyResponsiveStyles || isTinyScreen) && {
                    margin: '0 auto',
                }),
            }, children: [showImage && (_jsx(CardImage, { product: {
                        ...product,
                        image: (() => {
                            const finalImage = hoverImageUrl || currentImageUrl || mainImageUrl || image;
                            return finalImage;
                        })(),
                        title,
                    }, onClick: () => {
                        if (onProductClick && product && imageClickable) {
                            onProductClick(product);
                        }
                        else if (imageClickable) {
                            store.currentConversation?.sendTrackingEvent('product_click', {
                                product_name: title,
                                product_url: url,
                            });
                            window.open(url, '_top');
                        }
                    }, imageClickable: imageClickable, borderStyle: borderStyle, isHorizontal: isHorizontal, maxHeight: originalImageMaxHeight, objectFit: resolvedObjectFit })), _jsx(CardContent, { sx: {
                        px: 1,
                        pt: 1,
                        pb: 1,
                        overflow: 'visible',
                        minWidth: 0,
                        ...(!isHorizontal && {
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                        }),
                        '&:last-child': {
                            paddingBottom: 1,
                        },
                    }, children: _jsx(Stack, { direction: "column", spacing: stackSpacing, useFlexGap: true, sx: {
                            width: '100%',
                            ...(!isHorizontal && {
                                flex: 1,
                                minHeight: 0,
                            }),
                        }, children: allowedElements?.map((elementType) => (_jsx(React.Fragment, { children: elements[elementType] }, elementType))) }) })] })) }));
};
EnhancedProductCard.defaultProps = {
    customStyle: undefined,
    productFormatting: undefined,
    aiSearchConfig: undefined,
    price: '',
    description: '',
    onProductClick: undefined,
    product: undefined,
    forceVertical: undefined,
    initialVariantId: undefined,
};
export default EnhancedProductCard;
//# sourceMappingURL=EnhancedProductCard.js.map